import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import DashboardLayout from "../../Layout/DashboardLayout";
import {
  createUpdateRole,
  fetchRoles,
  fetchRolePrivileges,
  clearPrivileges,
} from "../../API_Service/Role_Slice";
import {
  hasPrivilege,
  PRIVILEGES,
  normalizeSpaces,
  CancelButton,
} from "../../Common/UIComponents";

const ROLE_NAME_MAX = 30;
const DESC_MAX = 250;

const CharCounter = ({ value, max }) => {
  const len = (value ?? "").length;
  if (len === 0) return null;
  const atLimit = len === max;
  return (
    <div
      className={`flex items-center gap-1 mt-1 text-xs ${atLimit ? "text-red-500 font-semibold" : "text-slate-400"}`}
    >
      <span>
        {len}/{max}
      </span>
      {atLimit && <span className="text-red-500">— limit reached</span>}
    </div>
  );
};

const CreateEditRolePage = ({ embedded = false, onCancel, onSuccess } = {}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  // Wrapper so callers can leave embedded={false} and still hit the same behaviour
  const handleCancel = () =>
    embedded ? onCancel?.() : navigate("/role-management");
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const companyId = user?.CompanyId || localStorage.getItem("companyId");
  const {
    // list: roles,
    privileges,
    privilegesLoading,
  } = useSelector((state) => state.role);
  const permissionRef = useRef(null);

  const roleCodeOptions = [
    { code: "PM", name: "Project Manager" },
    { code: "TM", name: "Team Manager" },
    { code: "PR", name: "Project Responsible" },
    { code: "QM", name: "Quality Manager" },
    { code: "TO", name: "Test Officer" },
    { code: "LM", name: "Lab Manager" },
    { code: "OT", name: "Others" },
  ];

  const [formData, setFormData] = useState({
    RoleID: "",
    RoleName: "",
    RoleCode: "",
    Description: "",
    StatusCode: "AC",
    JobDescription: "",
  });

  // Validation errors state
  const [errors, setErrors] = useState({
    RoleName: "",
    RoleCode: "",
    Privileges: "",
  });

  const [permissionSections, setPermissionSections] = useState([]);
  const [sectionSelectAll, setSectionSelectAll] = useState({});
  const [globalSelectAll, setGlobalSelectAll] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const isEditMode = !!(id && parseInt(id) > 0);
  const isInactiveEdit = isEditMode && formData.StatusCode !== "AC";

  // Clear stale privileges when leaving the page
  useEffect(() => {
    return () => {
      dispatch(clearPrivileges());
    };
  }, [dispatch]);

  // Fetch privileges for create (RoleID=0) or edit (RoleID=id)
  useEffect(() => {
    const roleId = id ? parseInt(id) : 0;
    if (companyId) {
      dispatch(
        fetchRolePrivileges({
          roleId: roleId,
          companyId: companyId,
        }),
      );
    }
  }, [dispatch, companyId, id]);

  // Process privileges into sections
  useEffect(() => {
    if (privileges && privileges.length > 0) {
      const moduleMap = new Map();

      privileges.forEach((privilege) => {
        const moduleName = privilege.ModuleName;
        const moduleCode = privilege.ModuleCode;
        const action = privilege.Action;
        const privilegeId = privilege.PrivilegeID;
        const isSelected = privilege.Selected && privilege.Selected !== "";

        if (!moduleName) return;

        if (!moduleMap.has(moduleName)) {
          moduleMap.set(moduleName, {
            id: moduleCode || moduleName.replace(/\s/g, ""),
            name: moduleName,
            icon: getIconForModule(moduleCode || moduleName),
            privilegeIds: {
              create: null,
              edit: null,
              delete: null,
              view: null,
            },
            permissions: {
              create: false,
              edit: false,
              delete: false,
              view: false,
            },
          });
        }

        const module = moduleMap.get(moduleName);

        if (action === "CREATE") {
          module.privilegeIds.create = privilegeId;
          module.permissions.create = isSelected;
        } else if (action === "EDIT") {
          module.privilegeIds.edit = privilegeId;
          module.permissions.edit = isSelected;
        } else if (action === "DELETE") {
          module.privilegeIds.delete = privilegeId;
          module.permissions.delete = isSelected;
        } else if (action === "VIEW") {
          module.privilegeIds.view = privilegeId;
          module.permissions.view = isSelected;
        }
      });

      const modulesArray = Array.from(moduleMap.values());

      const sections = [
        {
          id: "core",
          name: "User Management Modules",
          icon: "dashboard",
          modules: [],
          open: true,
        },
        {
          id: "management",
          name: "Management",
          icon: "settings",
          modules: [],
          open: true,
        },
        {
          id: "configuration",
          name: "Templates",
          icon: "build",
          modules: [],
          open: true,
        },
      ];

      modulesArray.forEach((module) => {
        if (module.name === "Employee" || module.name === "Role") {
          sections[0].modules.push(module);
        } else if (
          module.name === "Project" ||
          module.name === "Lab Planning" ||
          module.name === "Setup Utilization" ||
          module.name === "Measurement Protocol"
        ) {
          sections[1].modules.push(module);
        } else {
          sections[2].modules.push(module);
        }
      });

      const filteredSections = sections.filter(
        (section) => section.modules.length > 0,
      );

      setPermissionSections(filteredSections);

      const newSectionSelectAll = {};
      filteredSections.forEach((section) => {
        const allChecked = section.modules.every(
          (module) =>
            (!module.privilegeIds.create || module.permissions.create) &&
            (!module.privilegeIds.edit || module.permissions.edit) &&
            (!module.privilegeIds.delete || module.permissions.delete) &&
            (!module.privilegeIds.view || module.permissions.view),
        );
        newSectionSelectAll[section.id] = allChecked;
      });
      setSectionSelectAll(newSectionSelectAll);

      const allChecked =
        filteredSections.length > 0 &&
        filteredSections.every((section) =>
          section.modules.every(
            (module) =>
              (!module.privilegeIds.create || module.permissions.create) &&
              (!module.privilegeIds.edit || module.permissions.edit) &&
              (!module.privilegeIds.delete || module.permissions.delete) &&
              (!module.privilegeIds.view || module.permissions.view),
          ),
        );
      setGlobalSelectAll(allChecked);
    }
  }, [privileges]);

  const getIconForModule = (moduleCode) => {
    const iconMap = {
      EMP: "people",
      ROLE: "admin_panel_settings",
      PROJ: "assignment",
      EMAILTEMP: "email",
      SUEDIT: "settings_suggest",
      SUCREATE: "settings_suggest",
      SUVIEW: "settings_suggest",
      Lab: "biotech",
      Employee: "people",
      Role: "admin_panel_settings",
      Project: "assignment",
      "Email Template": "email",
      "Setup Utilization": "settings_suggest",
      "Lab Planning": "biotech",
    };
    return iconMap[moduleCode] || "extension";
  };

  // Fetch role basic data if editing
  useEffect(() => {
    if (!id || !companyId) return;
    dispatch(fetchRoles(companyId)).then((res) => {
      const data = res?.payload ?? [];
      const roleToEdit = data.find((r) => r.RoleID === parseInt(id));
      if (roleToEdit) {
        setFormData({
          RoleID: roleToEdit.RoleID,
          RoleName: roleToEdit.RoleName,
          RoleCode: roleToEdit.RoleCode || "",
          Description: roleToEdit.Description || "",
          StatusCode: roleToEdit.StatusCode || "AC",
          JobDescription: roleToEdit.JobDescription || "",
        });
      }
    });
  }, [id, companyId, dispatch]);

  // Clear field error when user types
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleRoleCodeChange = (e) => {
    const selectedCode = e.target.value;
    setFormData((prev) => ({
      ...prev,
      RoleCode: selectedCode,
    }));
    if (errors.RoleCode) {
      setErrors((prev) => ({ ...prev, RoleCode: "" }));
    }
  };

  // Validate form before save
  const validateForm = () => {
    const newErrors = {
      RoleName: "",
      RoleCode: "",
      Privileges: "",
    };
    let isValid = true;
    let firstErrorRef = null;

    if (!formData.RoleName.trim()) {
      newErrors.RoleName = "Role name is required";
      isValid = false;
    }

    if (!formData.RoleCode) {
      newErrors.RoleCode = "Role code is required";
      isValid = false;
    }

    const privilegeIDStr = generatePrivilegeIDStr();
    if (!privilegeIDStr) {
      newErrors.Privileges = "Please select at least one permission";
      firstErrorRef = firstErrorRef || permissionRef;
      isValid = false;
    }

    setErrors(newErrors);

    if (firstErrorRef?.current) {
      firstErrorRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      firstErrorRef.current.focus?.();
    }
    return isValid;
  };

  const toggleSection = (sectionIndex) => {
    const updatedSections = [...permissionSections];
    updatedSections[sectionIndex].open = !updatedSections[sectionIndex].open;
    setPermissionSections(updatedSections);
  };

  const hasPrivilege = (privilegeId) => {
    return privilegeId !== null && privilegeId !== undefined;
  };

  const handlePermissionChange = (
    sectionIndex,
    moduleIndex,
    action,
    checked,
  ) => {
    const updatedSections = [...permissionSections];
    const privilegeId =
      updatedSections[sectionIndex].modules[moduleIndex].privilegeIds[action];

    if (hasPrivilege(privilegeId)) {
      const mod = updatedSections[sectionIndex].modules[moduleIndex];
      mod.permissions[action] = checked;

      if (!checked && action === "view") {
        if (hasPrivilege(mod.privilegeIds.create))
          mod.permissions.create = false;
        if (hasPrivilege(mod.privilegeIds.edit)) mod.permissions.edit = false;
        if (hasPrivilege(mod.privilegeIds.delete))
          mod.permissions.delete = false;
      }

      // Auto-check View when Create, Edit, or Delete is checked
      if (checked && action !== "view" && hasPrivilege(mod.privilegeIds.view)) {
        mod.permissions.view = true;
      }

      setPermissionSections(updatedSections);

      // Clear privileges error when user selects something
      if (errors.Privileges) {
        setErrors((prev) => ({ ...prev, Privileges: "" }));
      }
    }

    const section = updatedSections[sectionIndex];
    const allChecked = section.modules.every(
      (module) =>
        (!module.privilegeIds.create || module.permissions.create) &&
        (!module.privilegeIds.edit || module.permissions.edit) &&
        (!module.privilegeIds.delete || module.permissions.delete) &&
        (!module.privilegeIds.view || module.permissions.view),
    );
    setSectionSelectAll((prev) => ({
      ...prev,
      [section.id]: allChecked,
    }));

    const allModulesChecked =
      updatedSections.length > 0 &&
      updatedSections.every((s) =>
        s.modules.every(
          (m) =>
            (!m.privilegeIds.create || m.permissions.create) &&
            (!m.privilegeIds.edit || m.permissions.edit) &&
            (!m.privilegeIds.delete || m.permissions.delete) &&
            (!m.privilegeIds.view || m.permissions.view),
        ),
      );
    setGlobalSelectAll(allModulesChecked);
  };

  const handleSectionSelectAll = (sectionIndex, checked) => {
    const updatedSections = [...permissionSections];
    updatedSections[sectionIndex].modules.forEach((module) => {
      module.permissions = {
        create: hasPrivilege(module.privilegeIds.create) ? checked : false,
        edit: hasPrivilege(module.privilegeIds.edit) ? checked : false,
        delete: hasPrivilege(module.privilegeIds.delete) ? checked : false,
        view: hasPrivilege(module.privilegeIds.view) ? checked : false,
      };
    });
    setPermissionSections(updatedSections);

    setSectionSelectAll((prev) => ({
      ...prev,
      [updatedSections[sectionIndex].id]: checked,
    }));

    const allModulesChecked =
      updatedSections.length > 0 &&
      updatedSections.every((s) =>
        s.modules.every(
          (m) =>
            (!m.privilegeIds.create || m.permissions.create) &&
            (!m.privilegeIds.edit || m.permissions.edit) &&
            (!m.privilegeIds.delete || m.permissions.delete) &&
            (!m.privilegeIds.view || m.permissions.view),
        ),
      );
    setGlobalSelectAll(allModulesChecked);

    // Clear privileges error when user selects something
    if (errors.Privileges) {
      setErrors((prev) => ({ ...prev, Privileges: "" }));
    }
  };

  const handleGlobalSelectAll = (checked) => {
    setGlobalSelectAll(checked);
    const updatedSections = [...permissionSections];
    updatedSections.forEach((section) => {
      section.modules.forEach((module) => {
        module.permissions = {
          create: hasPrivilege(module.privilegeIds.create) ? checked : false,
          edit: hasPrivilege(module.privilegeIds.edit) ? checked : false,
          delete: hasPrivilege(module.privilegeIds.delete) ? checked : false,
          view: hasPrivilege(module.privilegeIds.view) ? checked : false,
        };
      });
    });
    setPermissionSections(updatedSections);

    const newSectionSelectAll = {};
    updatedSections.forEach((section) => {
      const allChecked = section.modules.every(
        (m) =>
          (!m.privilegeIds.create || m.permissions.create) &&
          (!m.privilegeIds.edit || m.permissions.edit) &&
          (!m.privilegeIds.delete || m.permissions.delete) &&
          (!m.privilegeIds.view || m.permissions.view),
      );
      newSectionSelectAll[section.id] = allChecked;
    });
    setSectionSelectAll(newSectionSelectAll);

    // Clear privileges error when user selects something
    if (errors.Privileges) {
      setErrors((prev) => ({ ...prev, Privileges: "" }));
    }
  };

  const generatePrivilegeIDStr = () => {
    const selectedPrivilegeIds = [];

    permissionSections.forEach((section) => {
      section.modules.forEach((module) => {
        const { permissions, privilegeIds } = module;

        if (permissions.create && privilegeIds.create)
          selectedPrivilegeIds.push(privilegeIds.create);
        if (permissions.edit && privilegeIds.edit)
          selectedPrivilegeIds.push(privilegeIds.edit);
        if (permissions.delete && privilegeIds.delete)
          selectedPrivilegeIds.push(privilegeIds.delete);
        if (permissions.view && privilegeIds.view)
          selectedPrivilegeIds.push(privilegeIds.view);
      });
    });

    return selectedPrivilegeIds.join(",");
  };

  const handleSaveRole = async () => {
    // Validate form first
    if (!validateForm()) {
      return;
    }

    const privilegeIDStr = generatePrivilegeIDStr();

    setSaving(true);
    try {
      const isEdit = id && formData.RoleID;

      const roleData = {
        RoleID: isEdit ? parseInt(formData.RoleID) : 0,
        RoleCodeID: 0,
        SortOrder: 0,
        RoleName: normalizeSpaces(formData.RoleName),
        JobDescription: normalizeSpaces(formData.JobDescription || ""),
        CreatedBy: user?.UserId || user?.EmployeeID || 1,
        CreatedDate: isEdit ? new Date() : new Date().toISOString(),
        PrivilegeIDStr: privilegeIDStr,
        RoleCode: formData.RoleCode,
        Description: normalizeSpaces(formData.Description || ""),
        StatusCode: formData.StatusCode,
        SavedBy: user?.UserId || user?.EmployeeID || 1,
        SavedDate: new Date().toISOString(),
        PrivilegeIDs: privilegeIDStr,
      };

      setSaveError(null);
      const result = await dispatch(
        createUpdateRole({
          roleData,
          companyId: companyId,
        }),
      );

      if (result.error) {
        console.error("Error from dispatch:", result.error);
      } else {
        const raw = result?.payload?.data;
        const roleId = Array.isArray(raw) ? (raw[0]?.[""] ?? raw[0]) : raw;
        if (Number(roleId) === -1) {
          setSaveError(
            "A role with this name already exists. Please use a different name.",
          );
          return;
        }
        await dispatch(fetchRoles(companyId));
        if (embedded) {
          onSuccess?.(Number(roleId), formData.RoleName);
        } else {
          navigate("/role-management", {
            state: { refresh: true, editSuccess: true, isNew: !isEditMode },
          });
        }
      }
    } catch (error) {
      console.error("Error saving role:", error);
    } finally {
      setSaving(false);
    }
  };

  const totalPermissions = permissionSections.reduce(
    (sum, section) =>
      sum +
      section.modules.reduce(
        (s, module) =>
          s +
          Object.values(module.privilegeIds).filter((id) => id !== null).length,
        0,
      ),
    0,
  );

  const selectedPermissionsCount = permissionSections.reduce(
    (sum, section) =>
      sum +
      section.modules.reduce(
        (s, module) =>
          s +
          (module.permissions.create && module.privilegeIds.create ? 1 : 0) +
          (module.permissions.edit && module.privilegeIds.edit ? 1 : 0) +
          (module.permissions.delete && module.privilegeIds.delete ? 1 : 0) +
          (module.permissions.view && module.privilegeIds.view ? 1 : 0),
        0,
      ),
    0,
  );

  const Wrapper = embedded ? React.Fragment : DashboardLayout;

  if (privilegesLoading) {
    return (
      <Wrapper>
        <div className="p-8 min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      <div className="px-8 py-5 flex-1 w-full">
        {isInactiveEdit && (
          <div className="mb-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-xl px-5 py-4 text-sm flex items-center gap-3">
            <span className="material-symbols-outlined text-[20px]">info</span>
            <span>
              This role is <strong>Inactive</strong>. All fields are locked.
              Change the status to <strong>Active</strong> to enable editing.
            </span>
          </div>
        )}

        {saveError && (
          <div className="mb-4 bg-red-50 text-[#ba1a1a] rounded-xl px-5 py-4 text-sm flex items-center gap-3">
            <span className="material-symbols-outlined text-[20px]">error</span>
            {saveError}
          </div>
        )}

        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-5 gap-4">
          <div>
            {!embedded && (
              <nav className="flex items-center gap-2 text-xs text-[#3e494a] mb-2">
                <span
                  onClick={handleCancel}
                  className="cursor-pointer hover:text-[#08757c] transition-colors"
                >
                  Role Management
                </span>
                <span className="material-symbols-outlined text-[14px]">
                  chevron_right
                </span>
                <span className="text-[#08757c] font-semibold">
                  {id ? "Edit Role" : "Add New Role"}
                </span>
              </nav>
            )}
            <h2 className="text-3xl font-extrabold text-primary tracking-tight">
              {id ? "Edit Role" : "Create New Role"}
            </h2>
            <p className="text-slate-500 text-sm mt-1">
              {id
                ? "Update role details and permissions"
                : "Create user roles and assign module permissions"}
            </p>
          </div>
          <div className={`flex items-center gap-3 ${embedded ? "mr-12" : ""}`}>
            <CancelButton onClick={handleCancel} label="Cancel" />
            {(isEditMode
              ? hasPrivilege(PRIVILEGES.ROLE_EDIT)
              : hasPrivilege(PRIVILEGES.ROLE_CREATE)) && (
              <button
                onClick={handleSaveRole}
                disabled={saving}
                className="flex items-center gap-2 px-8 py-2.5 rounded-lg bg-primary text-white font-bold text-sm shadow-sm hover:bg-primary/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span className="material-symbols-outlined text-lg">
                  {saving ? "hourglass_empty" : "save"}
                </span>
                {saving
                  ? "Saving..."
                  : isEditMode
                    ? "Update Role"
                    : "Save Role"}
              </button>
            )}
          </div>
        </div>

        {/* Form Grid */}
        <div className="grid grid-cols-12 gap-6">
          {/* Left Column */}
          <div className="col-span-12 lg:col-span-5 space-y-4">
            {/* Identity & Classification Card */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined text-lg">
                    info
                  </span>
                </div>
                <h3 className="text-sm font-bold text-primary">
                  Identity & Classification
                </h3>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                    Role Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="RoleName"
                    value={formData.RoleName}
                    onChange={(e) => {
                      const sanitized = e.target.value.replace(
                        /[^a-zA-Z0-9 _-]/g,
                        "",
                      );
                      setFormData((prev) => ({ ...prev, RoleName: sanitized }));
                      if (errors.RoleName)
                        setErrors((prev) => ({ ...prev, RoleName: "" }));
                      setSaveError(null);
                    }}
                    disabled={isInactiveEdit}
                    className={`w-full px-4 py-3 bg-slate-50 border rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      errors.RoleName ? "border-red-500" : "border-slate-200"
                    }`}
                    placeholder="Enter role name"
                    maxLength={ROLE_NAME_MAX}
                  />
                  <CharCounter value={formData.RoleName} max={ROLE_NAME_MAX} />
                  {errors.RoleName && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.RoleName}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                    Role Code <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="RoleCode"
                    value={formData.RoleCode}
                    onChange={handleRoleCodeChange}
                    disabled={isInactiveEdit}
                    className={`w-full px-4 py-3 bg-slate-50 border rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      errors.RoleCode ? "border-red-500" : "border-slate-200"
                    }`}
                  >
                    <option value="">Select role code...</option>
                    {roleCodeOptions.map((option) => (
                      <option key={option.code} value={option.code}>
                        {option.code} - {option.name}
                      </option>
                    ))}
                  </select>
                  {errors.RoleCode && (
                    <p className="text-xs text-red-500 mt-1">
                      {errors.RoleCode}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-500 uppercase tracking-widest mb-2">
                    Status
                  </label>
                  <select
                    name="StatusCode"
                    value={formData.StatusCode}
                    onChange={handleInputChange}
                    disabled={!isEditMode}
                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <option value="AC">Active</option>
                    <option value="IN">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Description Card */}
            <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined text-lg">
                    description
                  </span>
                </div>
                <h3 className="text-sm font-bold text-primary">Description</h3>
              </div>
              <textarea
                name="Description"
                value={formData.Description}
                onChange={handleInputChange}
                rows="3"
                maxLength={DESC_MAX}
                disabled={isInactiveEdit}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Brief description of the role..."
              />
              <CharCounter value={formData.Description} max={DESC_MAX} />
            </div>

            {/* Job Description Card */}
            {/* <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                  <span className="material-symbols-outlined text-lg">
                    assignment
                  </span>
                </div>
                <h3 className="text-sm font-bold text-primary">
                  Job Description
                </h3>
              </div>
              <textarea
                name="JobDescription"
                value={formData.JobDescription}
                onChange={handleInputChange}
                rows="6"
                maxLength={DESC_MAX}
                disabled={isInactiveEdit}
                className="w-full px-4 py-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 font-medium focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                placeholder="Detailed job description, responsibilities, and requirements..."
              />
              <CharCounter value={formData.JobDescription} max={DESC_MAX} />
            </div> */}

            {/* Permission Summary Card */}
            <div
              ref={permissionRef}
              className="bg-emerald-50 border border-emerald-100 p-4 rounded-xl"
            >
              <div className="flex justify-between items-start">
                <div>
                  <h4 className="text-emerald-900 font-bold text-sm">
                    Permission Summary
                  </h4>
                  <p className="text-xs text-emerald-700 mt-1">
                    Total Permissions: {totalPermissions}
                    <br />
                    Selected Permissions: {selectedPermissionsCount}
                  </p>
                  {errors.Privileges && (
                    <p className="text-xs text-red-500 mt-2">
                      {errors.Privileges}
                    </p>
                  )}
                </div>
                <span className="material-symbols-outlined text-secondary">
                  verified_user
                </span>
              </div>
            </div>
          </div>

          {/* Right Column - Permissions Table */}
          <div className="col-span-12 lg:col-span-7">
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-4 border-b border-slate-200 flex justify-between items-center">
                <div>
                  <h3 className="text-sm font-bold text-primary flex items-center gap-2">
                    <span className="material-symbols-outlined text-lg">
                      vpn_key
                    </span>
                    Module Permissions
                  </h3>
                  <p className="text-xs text-slate-500 mt-1">
                    Define granular access levels across modules
                  </p>
                </div>
                <label
                  className={`flex items-center gap-2 bg-slate-50 px-3 py-1.5 rounded-lg border border-slate-200 ${isInactiveEdit ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                >
                  <input
                    type="checkbox"
                    checked={globalSelectAll}
                    onChange={(e) => handleGlobalSelectAll(e.target.checked)}
                    disabled={isInactiveEdit}
                    className="rounded border-slate-300 text-secondary accent-secondary focus:ring-secondary w-4 h-4 disabled:cursor-not-allowed"
                  />
                  <span className="text-xs font-bold text-slate-700">
                    SELECT ALL
                  </span>
                </label>
              </div>

              <div className="divide-y divide-slate-200 max-h-[600px] overflow-y-auto">
                {permissionSections.length === 0 ? (
                  <div className="p-8 text-center text-slate-500">
                    No permissions found
                  </div>
                ) : (
                  permissionSections.map((section, sectionIndex) => (
                    <div key={section.id} className="bg-white">
                      <button
                        type="button"
                        onClick={() => toggleSection(sectionIndex)}
                        className="w-full px-6 py-4 flex items-center justify-between hover:bg-slate-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className="material-symbols-outlined text-primary">
                            {section.icon}
                          </span>
                          <span className="font-bold text-slate-800">
                            {section.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-4">
                          <label
                            className="flex items-center gap-2 cursor-pointer"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <input
                              type="checkbox"
                              checked={sectionSelectAll[section.id] || false}
                              onChange={(e) =>
                                handleSectionSelectAll(
                                  sectionIndex,
                                  e.target.checked,
                                )
                              }
                              disabled={isInactiveEdit}
                              className="rounded border-slate-300 text-secondary accent-secondary focus:ring-secondary w-4 h-4 disabled:cursor-not-allowed"
                            />
                            <span className="text-xs text-slate-500">
                              Select All
                            </span>
                          </label>
                          <span className="material-symbols-outlined text-slate-400">
                            {section.open ? "expand_less" : "expand_more"}
                          </span>
                        </div>
                      </button>

                      {section.open && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left border-collapse">
                            <thead>
                              <tr className="bg-slate-50">
                                <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider">
                                  Module
                                </th>
                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">
                                  Create
                                </th>
                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">
                                  Edit
                                </th>
                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">
                                  Delete
                                </th>
                                <th className="px-4 py-3 text-xs font-bold text-slate-500 uppercase tracking-wider text-center">
                                  View
                                </th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {section.modules.map((module, moduleIndex) => (
                                <tr
                                  key={module.id}
                                  className="hover:bg-slate-50 transition-colors"
                                >
                                  <td className="px-6 py-3">
                                    <div className="flex items-center gap-3">
                                      <div className="w-8 h-8 rounded bg-slate-100 flex items-center justify-center">
                                        <span className="material-symbols-outlined text-primary text-sm">
                                          {module.icon}
                                        </span>
                                      </div>
                                      <span className="text-sm font-medium text-slate-700">
                                        {module.name}
                                      </span>
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {hasPrivilege(
                                      module.privilegeIds.create,
                                    ) ? (
                                      <input
                                        type="checkbox"
                                        checked={module.permissions.create}
                                        onChange={(e) =>
                                          handlePermissionChange(
                                            sectionIndex,
                                            moduleIndex,
                                            "create",
                                            e.target.checked,
                                          )
                                        }
                                        disabled={isInactiveEdit}
                                        className="rounded border-slate-300 text-secondary accent-secondary focus:ring-secondary w-4 h-4 disabled:cursor-not-allowed"
                                      />
                                    ) : (
                                      <span className="text-xs text-slate-400">
                                        —
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {hasPrivilege(module.privilegeIds.edit) ? (
                                      <input
                                        type="checkbox"
                                        checked={module.permissions.edit}
                                        onChange={(e) =>
                                          handlePermissionChange(
                                            sectionIndex,
                                            moduleIndex,
                                            "edit",
                                            e.target.checked,
                                          )
                                        }
                                        disabled={isInactiveEdit}
                                        className="rounded border-slate-300 text-secondary accent-secondary focus:ring-secondary w-4 h-4 disabled:cursor-not-allowed"
                                      />
                                    ) : (
                                      <span className="text-xs text-slate-400">
                                        —
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {hasPrivilege(
                                      module.privilegeIds.delete,
                                    ) ? (
                                      <input
                                        type="checkbox"
                                        checked={module.permissions.delete}
                                        onChange={(e) =>
                                          handlePermissionChange(
                                            sectionIndex,
                                            moduleIndex,
                                            "delete",
                                            e.target.checked,
                                          )
                                        }
                                        disabled={isInactiveEdit}
                                        className="rounded border-slate-300 text-secondary accent-secondary focus:ring-secondary w-4 h-4 disabled:cursor-not-allowed"
                                      />
                                    ) : (
                                      <span className="text-xs text-slate-400">
                                        —
                                      </span>
                                    )}
                                  </td>
                                  <td className="px-4 py-3 text-center">
                                    {hasPrivilege(module.privilegeIds.view) ? (
                                      <input
                                        type="checkbox"
                                        checked={module.permissions.view}
                                        onChange={(e) =>
                                          handlePermissionChange(
                                            sectionIndex,
                                            moduleIndex,
                                            "view",
                                            e.target.checked,
                                          )
                                        }
                                        disabled={isInactiveEdit}
                                        className="rounded border-slate-300 text-secondary accent-secondary focus:ring-secondary w-4 h-4 disabled:cursor-not-allowed"
                                      />
                                    ) : (
                                      <span className="text-xs text-slate-400">
                                        —
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default CreateEditRolePage;
